// Utility functions for formatting and UI updates in the taskpane
import { cleanFormatUtils } from "../utils/replaceStatements";

export function updateStatusIndicator(
  status: "loading" | "connected" | "error",
  message?: string
) {
  const indicator = document.getElementById("status-indicator");
  const dot = indicator?.querySelector(".status-dot") as HTMLElement | null;
  const text = indicator?.querySelector(".status-text") as HTMLElement | null;

  if (indicator && dot) {
    dot.className = "status-dot";
    dot.classList.add(status);

    if (text) {
      if (message) {
        text.textContent = message;
        text.style.display = "inline";
      } else {
        text.textContent = "";
        text.style.display = "none";
      }
    }
  }
}


export function formatMarkdownToHTML(text: string): string {
  
  text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  text = text.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>');

  text = text.replace(/`([^`\n]+?)`/g, '<code>$1</code>');

  text = text.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
  text = text.replace(/__([^_\n]+?)__/g, '<strong>$1</strong>');

  text = text.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
  text = text.replace(/(?<!_)_([^_\n]+?)_(?!_)/g, '<em>$1</em>');

  text = text.replace(/^### (.+)$/gm, '<h3><strong>$1</strong></h3>');
  text = text.replace(/^## (.+)$/gm, '<h2><strong>$1</strong></h2>');
  text = text.replace(/^# (.+)$/gm, '<h1><strong>$1</strong></h1>');

  // Split into lines for better list handling
  const lines = text.split('\n');
  const processedLines: string[] = [];
  let inList = false;
  let listType = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    if (/^\s*[-*]\s+/.test(line)) {
      if (!inList || listType !== 'ul') {
        if (inList) processedLines.push(`</${listType}>`);
        processedLines.push('<ul>');
        inList = true;
        listType = 'ul';
      }
      const content = line.replace(/^\s*[-*]\s+/, '');
      processedLines.push(`<li>${content}</li>`);
    }
    else if (/^\s*\d+\.\s+/.test(line)) {
      if (!inList || listType !== 'ol') {
        if (inList) processedLines.push(`</${listType}>`);
        processedLines.push('<ol>');
        inList = true;
        listType = 'ol';
      }
      const content = line.replace(/^\s*\d+\.\s+/, '');
      processedLines.push(`<li>${content}</li>`);
    }
    
    else {
      if (inList) {
        processedLines.push(`</${listType}>`);
        inList = false;
        listType = '';
      }
      processedLines.push(line);
    }
  }

  if (inList) {
    processedLines.push(`</${listType}>`);
  }

  text = processedLines.join('\n');

  const paragraphs = text.split(/\n\s*\n/);
  const formattedParagraphs = paragraphs.map(para => {
    const trimmed = para.trim();
    if (!trimmed) return '';
    
    if (/^<(h[1-6]|ul|ol|pre|div)/.test(trimmed)) {
      return trimmed;
    }
   
    const withBreaks = trimmed.replace(/\n/g, '<br>');
    return `<p>${withBreaks}</p>`;
  }).filter(p => p);

  return formattedParagraphs.join('\n\n');
}

export function isComplianceData(text: string): boolean {
  try {
    const parsed = JSON.parse(text);
    return !!(parsed.compliance_results || parsed.final_compliance_summary);
  } catch {
    return false;
  }
}

function addButtonsToRequirements(content: string, sectionId: string): string {
  const lines = content.split('\n');
  let result = '';
  let itemCounter = 0;
  
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    line = line.replace(/^###\s*(.+)$/, '<strong>$1</strong>');
    line = line.replace(/Top 5 critical gaps,?\s*/gi, '');
    line = line.replace(/Top 5 priority actions,?\s*/gi, '');
    line = line.replace(/Top 5\s+/gi, '');
    line = line.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    line = line.replace(/(OVERALL COMPLIANCE SCORE:\s*\d+\/\d+)/gi, '<strong>$1</strong>');
    
    // Make section titles bold (for content within boxes)
    line = line.replace(/^(MISSING REQUIREMENTS)$/gi, '<strong>$1</strong>');
    line = line.replace(/^(RECOMMENDATIONS)$/gi, '<strong>$1</strong>');
    line = line.replace(/(MISSING REQUIREMENTS\s*\([^)]*\))/gi, '<strong>$1</strong>');
    line = line.replace(/(RECOMMENDATIONS\s*\([^)]*\))/gi, '<strong>$1</strong>');
    
    // Updated regex to include bullets (- * •) and numbered lists
    if (/^(\d+\.|[-*•])\s/.test(line)) {
      itemCounter++;
      const uniqueId = `${sectionId}-${itemCounter}`;
      result += line + '<br>';
      result += `<div class="requirement-buttons" data-item="${uniqueId}">`;
      result += `<button class="req-btn accept-btn" onclick="showCommentBox('${uniqueId}', 'accept', '${line.replace(/'/g, "\\'").replace(/<[^>]*>/g, '')}', '${sectionId}')">Accept</button>`;
      result += `<button class="req-btn reject-btn" onclick="showCommentBox('${uniqueId}', 'reject', '${line.replace(/'/g, "\\'").replace(/<[^>]*>/g, '')}', '${sectionId}')">Reject</button>`;
      result += `<button class="req-btn ignore-btn" onclick="showCommentBox('${uniqueId}', 'ignore', '${line.replace(/'/g, "\\'").replace(/<[^>]*>/g, '')}', '${sectionId}')">Ignore</button>`;
      result += '</div>';
      result += `<div class="comment-box" id="comment-${uniqueId}" style="display: none;">`;
      result += '<textarea class="comment-input" placeholder="Add your comment..."></textarea>';
      result += '<div class="comment-actions">';
      result += `<button class="comment-btn ok-btn" onclick="handleComment('${uniqueId}', 'ok')">OK</button>`;
      result += `<button class="comment-btn cancel-btn" onclick="handleComment('${uniqueId}', 'cancel')">Cancel</button>`;
      result += '</div></div>';
      result += `<div class="submitted-comment" id="submitted-${uniqueId}" style="display: none;"></div><br>`;
    } else if (line && !line.includes('(≤80 words)')) {
      result += line + '<br>';
    }
  }
  
  return result;
}

function formatRequirementsWithActions(content: string, type: string): string {
  let title = '';
  if (type === 'satisfied') title = 'Satisfied Requirements';
  else if (type === 'missing') title = 'Missing Requirements';
  else if (type === 'recommendations') title = 'Recommendations';
  
  return `<div class="compliance-box ${type}-box">
    <h4>${title}</h4>
    <div class="box-content">${addButtonsToRequirements(content, type)}</div>
  </div>`;
}

export function formatComplianceData(text: string): string {
  try {
    const data = JSON.parse(text);
    let html = '';
    const isDetailedCheck = data.detailed_check === true;
    const idPrefix = isDetailedCheck ? 'detail-' : '';

    if (data.compliance_results) {
      const results = data.compliance_results;
      html = '<div class="compliance-results">';

      if (results.status === "analysis_incomplete") {
        html += `<p><strong>Status:</strong> ${results.status}</p>`;
        html += `<p><strong>Message:</strong> ${results.message}</p>`;
      } else {
        if (results.compliance_status) {
          html += `<p><strong>Status:</strong> ${results.compliance_status}</p>`;
        }

        // Satisfied Requirements Box (Green)
        if (results.satisfied_requirements) {
          html += formatRequirementsWithActions(results.satisfied_requirements, idPrefix + 'satisfied');
        }

        // Missing Requirements Box (Orange)
        if (results.missing_requirements) {
          html += formatRequirementsWithActions(results.missing_requirements, idPrefix + 'missing');
        }

        // Recommendations Box (Purple)
        if (results.recommendations) {
          html += formatRequirementsWithActions(results.recommendations, idPrefix + 'recommendations');
        }
      }

      html += '</div>';
    } else if (data.final_compliance_summary) {
      const summary = data.final_compliance_summary;
      html = '<div class="compliance-summary" style="margin: 0 !important; padding: 0 !important;">';

      html += '<div class="compliance-box summary-box"><h4>🧾 Final Compliance Summary</h4><div class="box-content">';
      if (summary.cfr21_status) {
        html += `<p style="margin: 0 !important; padding: 0 !important;"><strong>CFR21 Status:</strong> ${summary.cfr21_status}</p>`;
      }
      if (summary.satisfied_requirements) {
        html += formatRequirementsWithActions(summary.satisfied_requirements, idPrefix + 'satisfied');
      }
      if (summary.missing_requirements) {
        html += formatRequirementsWithActions(summary.missing_requirements, idPrefix + 'missing');
      }
      if (summary.recommendations) {
        html += formatRequirementsWithActions(summary.recommendations, idPrefix + 'recommendations');
      }

      if (summary.subpart_scores) {
        html += '<h4>Subpart Scores</h4><ul>';
        for (const [subpart, score] of Object.entries(summary.subpart_scores)) {
          html += `<li><span class="score">${subpart}: ${score}%</span></li>`;
        }
        html += '</ul>';
      }

      html += '</div>';
    } else {
      html = formatMarkdownToHTML(text);
    }

    return html;
  } catch (error) {
    console.error('Error formatting compliance data:', error);
    return formatMarkdownToHTML(text);
  }
}

function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

function cleanText(str: string): string {
  if (!str) return '';

  let cleaned = decodeHtmlEntities(str);
 
  return cleanFormatUtils(cleaned);

  cleaned = cleaned.split('\n').filter(line => {
    const printableChars = line.replace(/[^\x20-\x7E]+/g, '');
    return printableChars.trim().length > 0 || line.trim() === '';
  }).join('\n');

  return cleaned.trim();
}

export function formatComplianceDataForPDF(text: string): string {
  try {
    const data = JSON.parse(text);
    let output = '';
    let processedSections = new Set();

    if (data.compliance_results) {
      const results = data.compliance_results;

      output += '\n';

      if (results.status === "analysis_incomplete") {
        output += `Status: ${cleanText(results.status)}\n`;
        output += `Message: ${cleanText(results.message)}\n`;
      } else {
        if (results.compliance_status) {
          output += `Status: ${cleanText(results.compliance_status)}\n`;
        }

        if (results.overall_score !== undefined) {
          output += `Compliance Score: ${results.overall_score}%\n\n`;
        }

        if (results.satisfied_requirements && !processedSections.has('satisfied')) {
          output += `Satisfied Requirements\n${cleanText(results.satisfied_requirements)}\n\n`;
          processedSections.add('satisfied');
        }

        if (results.missing_requirements && !processedSections.has('missing')) {
          output += `Missing Requirements\n${cleanText(results.missing_requirements)}\n\n`;
          processedSections.add('missing');
        }

        if (results.recommendations && !processedSections.has('recommendations')) {
          output += `Recommendations\n${cleanText(results.recommendations)}\n\n`;
          processedSections.add('recommendations');
        }
      }
    } else if (data.final_compliance_summary) {
      const summary = data.final_compliance_summary;

      output += 'Final Compliance Summary:\n\n';

      if (summary.cfr21_status) {
        output += `CFR21 Status: ${cleanText(summary.cfr21_status)}\n`;
      }

      if (summary.overall_compliance_score !== undefined) {
        output += `Compliance Score: ${summary.overall_compliance_score}%\n\n`;
      }

      if (summary.satisfied_requirements && !processedSections.has('satisfied')) {
        output += `Satisfied Requirements\n${cleanText(summary.satisfied_requirements)}\n\n`;
        processedSections.add('satisfied');
      }

      if (summary.missing_requirements && !processedSections.has('missing')) {
        output += `Missing Requirements\n${cleanText(summary.missing_requirements)}\n\n`;
        processedSections.add('missing');
      }

      if (summary.recommendations && !processedSections.has('recommendations')) {
        output += `Recommendations\n${cleanText(summary.recommendations)}\n\n`;
        processedSections.add('recommendations');
      }

      if (summary.subpart_scores) {
        output += 'Subpart Scores:\n';
        for (const [subpart, score] of Object.entries(summary.subpart_scores)) {
          output += `${cleanText(subpart)}: ${score}%\n`;
        }
        output += '\n';
      }
    } else {
      output += cleanText(text);
    }

    return cleanText(output);
  } catch (error) {
    console.error('Error formatting compliance data for PDF:', error);
    return cleanText(text);
  }
}

export function updateComplianceScore(text: string): void {
  try {
    const data = JSON.parse(text);
    let score: number | undefined;

    if (data.compliance_results) {
      score = data.compliance_results.overall_score;
    } else if (data.final_compliance_summary) {
      score = data.final_compliance_summary.overall_compliance_score;
    }

    const scoreElement = document.getElementById("compliance-score");
    if (scoreElement && score !== undefined) {
      scoreElement.innerHTML = `${score}%`;
      scoreElement.style.display = "inline";
    }
  } catch (error) {
    console.error('Error updating compliance score:', error);
  }
}
